import { createSelector } from 'reselect'
import { denormalize, Schema } from 'normalizr'
import { values, flatten, get, forEach, find, isNil, compact } from 'lodash'

import { DEFAULT_LIST_KEY } from 'store/common/constants/generic'
import { State, Options } from 'types/store'

/**
 * Get current store state
 *
 * @private
 * @returns {function}
 *
 * @example:
 *
 * const getState = makeGetState()
 */
export const makeGetState =
  () =>
  (state: State): State =>
    state

/**
 * Get current options, which were passed to function born from selector creator
 *
 * @private
 * @returns {function}
 *
 * @example:
 *
 * const getOptions = makeGetOptions()
 */
export const makeGetOptions =
  () =>
  (state: State, options: Options): Options =>
    options

/**
 * Get all items from store entities by key as a collection
 *
 * @returns {function}
 *
 * @example:
 *
 * const getStatics = makeGetStatics()
 */
export const makeGetStatics =
  () =>
  (state: State): Record<string, any> =>
    state?.statics || {}

/**
 * Get statics from store
 *
 * @param staticEntityKey - {string}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getMe = makeGetStaticsEntity({ staticEntityKey: 'me' })
 */
export const makeGetStaticsEntity = ({ staticEntityKey }: { staticEntityKey: string }) => {
  const getStaticsSelector = makeGetStatics()

  return createSelector([getStaticsSelector], (staticEntity: Record<string, any>) => get(staticEntity, staticEntityKey))
}

/**
 * Get all items from store entities by key as a collection
 *
 * @param entityKey - {string}
 * @param [schema] - {object}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsersCollection = makeGetEntitiesCollection({ entityKey: 'users' })
 */
export const makeGetEntitiesCollection =
  ({ entityKey, schema }: { entityKey: string; schema?: Schema }) =>
  (state: State): Record<string, any> => {
    const entities = get(state, `entities.${entityKey}`, {})
    const entitiesDenormalized: Record<string, any> = {}

    if (isNil(schema)) {
      return entities
    }

    forEach(entities, (entity: any, key: string) => {
      entitiesDenormalized[key] = denormalize(entity, schema, state.entities)
    })

    return entitiesDenormalized
  }

/**
 * Get all items from store entities by key as an array
 *
 * @param entityKey - {string}
 * @param [schema] - {object}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsersCollection = makeGetEntitiesCollection({ entityKey: 'users', [schema] })
 */
export const makeGetEntitiesArray =
  ({ entityKey, schema }: { entityKey: string; schema?: Schema }) =>
  (state: State): any[] => {
    const entities = values(get(state, `entities.${entityKey}`, {})) || []
    const entitiesDenormalized: any[] = []

    if (isNil(schema)) {
      return entities
    }

    forEach(entities, (entity: any) => {
      entitiesDenormalized.push(denormalize(entity, schema, state.entities))
    })

    return entitiesDenormalized
  }

/**
 * Get all list pages from store for particular entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getAbsencesPages = makeGetEntityListPages({ entityKey: 'absences' })
 * const getAbsencesHistoryPages = makeGetEntityListPages({ entityKey: 'absences', entityListKey: 'history' })
 */
export const makeGetEntityListPages =
  ({ entityKey, entityListKey = DEFAULT_LIST_KEY }: { entityKey: string; entityListKey?: string }) =>
  (state: State): Record<string, string[]> =>
    get(state, `lists.${entityKey}.${entityListKey}.pages`, {})

/**
 * Get all list meta from store for particular entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getAbsencesMeta = makeGetEntityListMeta({ entityKey: 'absences' })
 * const getAbsencesHistoryMeta = makeGetEntityListMeta({ entityKey: 'absences', entityListKey: 'history' })
 */
export const makeGetEntityListMeta =
  ({ entityKey, entityListKey = DEFAULT_LIST_KEY }: { entityKey: string; entityListKey?: string }) =>
  (state: State): Record<string, any> =>
    get(state, `lists.${entityKey}.${entityListKey}.meta`, {})

/**
 * Get list of ids for all pages for particular entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getAbsencesMeta = makeGetEntityListMeta({ entityKey: 'absences' })
 * const getAbsencesHistoryMeta = makeGetEntityListMeta({ entityKey: 'absences', entityListKey: 'history' })
 */
export const makeGetEntityListIds = ({
  entityKey,
  entityListKey = DEFAULT_LIST_KEY,
}: {
  entityKey: string
  entityListKey?: string
}) => {
  const getEntityListPages = makeGetEntityListPages({ entityKey, entityListKey })

  return createSelector([getEntityListPages], (listPages: Record<string, string[]>) => flatten(values(listPages)))
}

/**
 * Get entity from store by Id
 *
 * @param entityKey - {string}
 * @param pristineData - {object}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUserById = makeGetEntityById({ entityKey: 'users', pristineData: {}, schema: userSchema  })
 *
 * export const mapStateToProps = state => ({
 *    user: userSelectors.getUserById(state, {
 *      id: get(state, 'router.params.id')
 *    }),
 * })
 */
export const makeGetEntityById = ({
  entityKey,
  pristineData = {},
  schema,
}: {
  entityKey: string
  pristineData?: any
  schema?: Schema
}) => {
  const getState = makeGetState()
  const getOptions = makeGetOptions()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })

  return createSelector(
    [getState, getOptions, getEntitiesCollectionSelector],
    (state: State, options: Options, entities: Record<string, any>) => {
      const id = get(options, 'id', 0)
      const entity = get(entities, `${id}`, pristineData)

      return denormalize(entity, schema, state.entities)
    },
  )
}

/**
 * Get entity from store by Filter
 *
 * @param entityKey - {string}
 * @param pristineData - {object}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUserByFilter = makeGetEntityByFilter({ entityKey: 'users', pristineData: {}, schema: userSchema  })
 *
 * export const mapStateToProps = state => ({
 *    user: userSelectors.getUserByFilter(state, {
 *      filter: {
 *        admin: true
 *      }
 *    }),
 * })
 */
export const makeGetEntityByFilter = ({
  entityKey,
  pristineData = {},
  schema,
}: {
  entityKey: string
  pristineData?: any
  schema?: Schema
}) => {
  const getState = makeGetState()
  const getOptions = makeGetOptions()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })

  return createSelector(
    [getState, getOptions, getEntitiesCollectionSelector],
    (state: State, options: Options, entities: Record<string, any>) => {
      const filterParams = get(options, 'filter', {})
      const entity = find(entities, filterParams) || pristineData

      return denormalize(entity, schema, state.entities)
    },
  )
}

/**
 * Get all entities from all available pages in the list by entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsers = makeGetEntitiesArrayFromList({ entityKey: 'users' schema: userSchema  })
 * const getDeletedUsers = makeGetEntitiesArrayFromList({
 *    entityKey: 'users', entityListKey: 'deleted', schema: userSchema
 * })
 *
 * export const mapStateToProps = state => ({
 *    users: userSelectors.getUsers(state),
 *    usersWithStatusDeleted: userSelectors.getDeletedUsers(state),
 * })
 */
export const makeGetEntitiesArrayFromList = ({
  entityKey,
  entityListKey = DEFAULT_LIST_KEY,
  schema,
}: {
  entityKey: string
  entityListKey?: string
  schema?: Schema
}) => {
  const getState = makeGetState()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })
  const getEntityListIds = makeGetEntityListIds({ entityKey, entityListKey })

  return createSelector(
    [getState, getEntitiesCollectionSelector, getEntityListIds],
    (state: State, entities: Record<string, any>, ids: string[]) =>
      compact(ids.map((id: string) => denormalize(entities[id], schema, state.entities))),
  )
}

/**
 * Returns collection of entities by pages
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsers = makeGetEntitiesCollectionFromList({ entityKey: 'users' schema: userSchema  })
 * const getDeletedUsers = makeGetEntitiesCollectionFromList({
 *    entityKey: 'users', entityListKey: 'deleted', schema: userSchema
 * })
 *
 * export const mapStateToProps = state => ({
 *    users: userSelectors.getUsers(state),
 *    usersWithStatusDeleted: userSelectors.getDeletedUsers(state),
 * })
 */
export const makeGetEntitiesCollectionFromList = ({
  entityKey,
  entityListKey = DEFAULT_LIST_KEY,
  schema,
}: {
  entityKey: string
  entityListKey?: string
  schema?: Schema
}) => {
  const getState = makeGetState()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })
  const getEntityListIds = makeGetEntityListIds({ entityKey, entityListKey })

  return createSelector(
    [getState, getEntitiesCollectionSelector, getEntityListIds],
    (state: State, entities: Record<string, any>, ids: string[]) => {
      const entitiesDenormalized: Record<string, any> = {}

      forEach(ids, (id: string) => {
        entitiesDenormalized[id] = denormalize(entities[id], schema, state.entities)
      })

      return entitiesDenormalized
    },
  )
}

/**
 * Get entities by page in the list by entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsersByPage = makeGetEntitiesArrayFromListByPage({ entityKey: 'users' schema: userSchema  })
 * const getDeletedUsersByPage = makeGetEntitiesArrayFromListByPage({
 *    entityKey: 'users', entityListKey: 'deleted', schema: userSchema
 * })
 *
 * export const mapStateToProps = state => ({
 *    users: userSelectors.getUsersByPage(state, {
 *      page: get(state, 'router.params.page')
 *    }),
 *    usersWithStatusDeleted: userSelectors.getDeletedUsersByPage(state, {
 *      page: get(state, 'router.params.page')
 *    })
 * })
 */
export const makeGetEntitiesArrayFromListByPage = ({
  entityKey,
  entityListKey = DEFAULT_LIST_KEY,
  schema,
}: {
  entityKey: string
  entityListKey?: string
  schema?: Schema
}) => {
  const getState = makeGetState()
  const getOptions = makeGetOptions()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })
  const getEntityListPages = makeGetEntityListPages({ entityKey, entityListKey })

  return createSelector(
    [getState, getOptions, getEntitiesCollectionSelector, getEntityListPages],
    (state: State, options: Options, entities: Record<string, any>, pages: Record<string, string[]>) => {
      const page = get(options, 'page', 0)
      const ids = pages[page] || []

      return compact(ids.map((id: string) => denormalize(entities[id], schema, state.entities)))
    },
  )
}

/**
 * Get entities before page in the list by entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 * @param schema - {function}
 * @returns {function}
 * @example:
 *
 * const getUsersByPage = makeGetEntitiesArrayFromListBeforePage({ entityKey: 'users' schema: userSchema  })
 * const getDeletedUsersByPage = makeGetEntitiesArrayFromListBeforePage({
 *    entityKey: 'users', entityListKey: 'deleted', schema: userSchema
 * })
 *
 * export const mapStateToProps = state => ({
 *    users: userSelectors.getUsersByPage(state, {
 *      page: get(state, 'router.params.page')
 *    }),
 *    usersWithStatusDeleted: userSelectors.getDeletedUsersByPage(state, {
 *      page: get(state, 'router.params.page')
 *    })
 * })
 */
export const makeGetEntitiesArrayFromListBeforePage = ({
  entityKey,
  entityListKey = DEFAULT_LIST_KEY,
  schema,
}: {
  entityKey: string
  entityListKey?: string
  schema?: Schema
}) => {
  const getState = makeGetState()
  const getOptions = makeGetOptions()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })
  const getEntityListPages = makeGetEntityListPages({ entityKey, entityListKey })

  return createSelector(
    [getState, getOptions, getEntitiesCollectionSelector, getEntityListPages],
    (state: State, options: Options, entities: Record<string, any>, pages: Record<string, string[]>) => {
      const page = get(options, 'page', 0)
      const ids: string[] = []

      forEach(pages, (value: string[], key: string) => {
        if (key <= page) {
          ids.push(...value)
        }
      })

      return compact(ids.map((id: string) => denormalize(entities[id], schema, state.entities)))
    },
  )
}
